import React from 'react';
import { isScenarioAnswerCorrect } from './scenarioResultFormat';

/** 동공 반응 행 — 환자 단위 집계용 (PupilResult) */
export interface PupilPerfRowLike {
  no?: number;
  name?: string;
  gender?: string;
  age?: string | number;
  position?: string;
  pupil_size?: string | number;
  pupil_reaction?: string;
  pupil_shape?: string;
  correct_pupil_size?: string | number;
  correct_pupil_reaction?: string;
  correct_pupil_shape?: string;
  result?: string;
  completed_missed?: string;
  comment?: string;
}

export interface PupilPatientAggRow {
  no?: number;
  name?: string;
  gender?: string;
  age?: string | number;
  L: PupilPerfRowLike | null;
  R: PupilPerfRowLike | null;
  result?: string;
  comment?: string;
}

export function groupPupilPerformanceByPatient(performance: PupilPerfRowLike[]): PupilPatientAggRow[] {
  const byPatient = performance.reduce(
    (acc: Record<string, PupilPatientAggRow>, row: PupilPerfRowLike) => {
      const key = `${row.no ?? ''}_${row.name ?? ''}`;
      if (!acc[key]) {
        acc[key] = {
          no: row.no,
          name: row.name,
          gender: row.gender,
          age: row.age,
          L: null,
          R: null,
          result: row.result ?? row.completed_missed,
          comment: row.comment,
        };
      }
      if (row.position === 'L') acc[key].L = row;
      if (row.position === 'R') acc[key].R = row;
      return acc;
    },
    {},
  );
  return Object.values(byPatient);
}

function getPupilReactionText(type: number | string | null | undefined): string {
  const num = typeof type === 'number' ? type : Number(type);
  switch (num) {
    case 0: return 'Prompt';
    case 1: return 'Sluggish';
    case 2: return 'Fixed';
    case 3: return 'Hippus';
    default: return '';
  }
}

function getPupilShapeText(type: number | string | null | undefined): string {
  const num = typeof type === 'number' ? type : Number(type);
  switch (num) {
    case 0: return 'Round';
    case 1: return 'Ovoid';
    case 2: return 'Irregular';
    default: return '';
  }
}

export function PupilEyeValueCell({ r }: { r: PupilPerfRowLike | null }): React.ReactNode {
  if (!r) return '-';
  
  const sizeNum = r.pupil_size != null ? Number(r.pupil_size) : null;
  const reactionNum = r.pupil_reaction != null ? Number(r.pupil_reaction) : null;
  const shapeNum = r.pupil_shape != null ? Number(r.pupil_shape) : null;
  
  const correctSizeNum = r.correct_pupil_size != null ? Number(r.correct_pupil_size) : null;
  const correctReactionNum = r.correct_pupil_reaction != null ? Number(r.correct_pupil_reaction) : null;
  const correctShapeNum = r.correct_pupil_shape != null ? Number(r.correct_pupil_shape) : null;
  
  // 크기는 ±1 오차 허용
  const sizeWrong = correctSizeNum != null && sizeNum != null && Math.abs(sizeNum - correctSizeNum) > 1;
  const reactionWrong = correctReactionNum != null && reactionNum !== correctReactionNum;
  const shapeWrong = correctShapeNum != null && shapeNum !== correctShapeNum;
  
  const hasCorrectAny = correctSizeNum != null || correctReactionNum != null || correctShapeNum != null;
  const rowWrong = !isScenarioAnswerCorrect(r.result ?? r.completed_missed);
  
  const parts: React.ReactNode[] = [];
  
  // 크기 (mm 단위)
  if (sizeNum != null) {
    const sizeText = `${sizeNum}mm`;
    parts.push(sizeWrong ? <span key="s" className="text-red-600 font-medium">{sizeText}</span> : sizeText);
  }
  
  // 반응
  if (reactionNum != null) {
    const reactionText = getPupilReactionText(reactionNum);
    if (reactionText) {
      parts.push(reactionWrong ? <span key="r" className="text-red-600 font-medium">{reactionText}</span> : reactionText);
    }
  }
  
  // 형태
  if (shapeNum != null) {
    const shapeText = getPupilShapeText(shapeNum);
    if (shapeText) {
      parts.push(shapeWrong ? <span key="sh" className="text-red-600 font-medium">{shapeText}</span> : shapeText);
    }
  }
  
  if (parts.length === 0) return '-';
  if (hasCorrectAny) return <>{parts.map((p, i) => <React.Fragment key={i}>{i > 0 ? ' ' : null}{p}</React.Fragment>)}</>;
  
  // 전체 오답인 경우
  if (rowWrong) {
    const allText: string[] = [];
    if (sizeNum != null) allText.push(`${sizeNum}mm`);
    if (reactionNum != null) allText.push(getPupilReactionText(reactionNum));
    if (shapeNum != null) allText.push(getPupilShapeText(shapeNum));
    return <span className="text-red-600 font-medium">{allText.filter(Boolean).join(' ')}</span>;
  }
  
  const allText: string[] = [];
  if (sizeNum != null) allText.push(`${sizeNum}mm`);
  if (reactionNum != null) allText.push(getPupilReactionText(reactionNum));
  if (shapeNum != null) allText.push(getPupilShapeText(shapeNum));
  return allText.filter(Boolean).join(' ');
}

export function MotorLimbValueCell({
  row,
  limbKey,
}: {
  row: Record<string, unknown>;
  limbKey: 'left_arm' | 'right_arm' | 'left_leg' | 'right_leg';
}): React.ReactNode {
  const userVal = row[limbKey] != null ? String(row[limbKey]) : '-';
  const correctVal = row[`correct_${limbKey}`];
  const isWrong = correctVal != null && userVal !== String(correctVal);
  if (userVal === '-') return <span className="text-gray-500">-</span>;
  return isWrong ? <span className="text-red-600 font-medium">{userVal}</span> : userVal;
}

export function GcsComparedValueCell({
  correctVal,
  userVal,
}: {
  correctVal: unknown;
  userVal: unknown;
}): React.ReactNode {
  const correct = correctVal != null ? String(correctVal) : null;
  const user = userVal != null ? String(userVal) : null;
  const isCorrect = correct === user || (user != null && correct == null);
  if (user == null && correct == null) return <span className="text-gray-500">-</span>;
  if (isCorrect) return <span>{user}</span>;
  return <span className="text-red-600 font-medium">{user}</span>;
}

/** ProcedureView — 정답(처방·선택) 열 본문 */
export function ProcedureReferenceCellContent({ row }: { row: Record<string, unknown> }): React.ReactNode {
  const contentItems = row.contentItems as Array<{ label?: string; isError?: boolean }> | undefined;
  if (contentItems?.length) {
    return (
      <span className="flex flex-col gap-y-1">
        {contentItems.map((item, i: number) => (
          <span key={i} className={item.isError ? 'text-red-600 font-medium' : ''}>
            {item.label}
          </span>
        ))}
      </span>
    );
  }
  const contentParts = row.contentParts as Array<{ text?: string; isError?: boolean }> | undefined;
  if (contentParts?.length) {
    return (
      <span className="inline-flex flex-wrap gap-x-0.5">
        {contentParts.map((part, i: number) => (
          <span key={i} className={part.isError ? 'text-red-600 font-medium' : ''}>{part.text}</span>
        ))}
      </span>
    );
  }
  const prescribed = row.prescribed;
  const userSelection = row.userSelection;
  const hasPrescribed = prescribed != null && prescribed !== '';
  const hasUserSelection = userSelection != null && userSelection !== '';
  const wrong = !isScenarioAnswerCorrect(row.result);
  if (hasPrescribed || hasUserSelection) {
    return (
      <>
        {hasPrescribed && <div className="mb-1">{prescribed as React.ReactNode}</div>}
        {hasUserSelection && (
          <div className={wrong ? 'text-red-600 font-medium' : ''}>{userSelection as React.ReactNode}</div>
        )}
      </>
    );
  }
  const content = row.content;
  if (content != null && content !== '') return content as React.ReactNode;
  return '-';
}
